import './PacingSnapshotChart.scss';
import cn from 'classnames';
import { JSX, useState } from 'react';
import { formatNumberAsInt } from '@blisspointmedia/bpm-types/dist/formatUtils';

const DEFAULT_FORMATTER = (value: number): string => formatNumberAsInt(value);

export const BarType = {
  HORIZONTAL: 'HORIZONTAL',
  VERTICAL: 'VERTICAL',
  HORIZONTAL_GREY: 'HORIZONTAL_GREY'
} as const;
export type BarTypeType = (typeof BarType)[keyof typeof BarType];

export interface BarPart {
  className?: string;
  label?: string | JSX.Element;
  leftVal?: number;
  type: BarTypeType;
  val?: number;
}

interface PacingBarProps {
  max?: number;
  parts: BarPart[];
  color: string;
}

export interface DataItem {
  label: string;
  subLabel: string | JSX.Element;
  topValue: number;
  bottomValue?: number;
  parts: BarPart[];
  color: string;
}

interface PacingSnapshotChartProps {
  data: DataItem[];
  doubleBarChart?: boolean;
  valueFormatter?: (value: number) => string;
}

interface LabelsProps {
  label: string;
  subLabel: string | JSX.Element;
  hovered: boolean;
}

interface ValuesProps {
  topValue: number;
  bottomValue?: number;
  doubleBarChart?: boolean;
  valueFormatter: (value: number) => string;
}

// `minHeight` (not `height`) — a channel name long enough to wrap to 3 lines needs the box to
// grow past the normal 2-line size instead of clipping and having the hover sub-label spill
// into the row below.
const Labels = ({ label, subLabel, hovered }: LabelsProps) => (
  <div className='snapshot-labels-left' style={{ minHeight: 45 }}>
    <div className='item-label'>{label}</div>
    {hovered && <div className='item-sub-label'>{subLabel}</div>}
  </div>
);

const Values = ({ topValue, bottomValue, doubleBarChart, valueFormatter }: ValuesProps) => (
  <div
    className={cn('snapshot-values-right', { 'double-bar-chart': doubleBarChart })}
    style={{ minHeight: 45 }}
  >
    <div className='top-value'>{valueFormatter(topValue)}</div>
    <div className='bottom-value'>{valueFormatter(bottomValue || 0)}</div>
  </div>
);

export const PacingSnapshotChart = ({
  data,
  doubleBarChart,
  valueFormatter = DEFAULT_FORMATTER
}: PacingSnapshotChartProps) => {
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);

  const maxValue = data.reduce((acc, item) => {
    const { parts } = item;
    const partsMax = parts.reduce((partsAcc, part) => Math.max(partsAcc, part?.val || 0), 0);
    return Math.max(acc, partsMax);
  }, 0);

  return (
    <div id='pacing-snapshot-chart' className='pacing-snapshot-chart'>
      {data.map(item => {
        const { label, subLabel, topValue, bottomValue, parts, color } = item;
        const hovered = hoveredRow === label;
        return (
          <div
            key={label}
            className={cn('snapshot-row', { hovered })}
            onMouseEnter={() => setHoveredRow(label)}
            onMouseLeave={() => setHoveredRow(null)}
          >
            <Labels label={label} subLabel={subLabel} hovered={hovered} />
            {doubleBarChart ? (
              <DoubleBar parts={parts} max={maxValue} color={color} />
            ) : (
              <PacingBar parts={parts} max={maxValue} color={color} />
            )}
            <Values
              topValue={topValue}
              bottomValue={bottomValue}
              doubleBarChart={doubleBarChart}
              valueFormatter={valueFormatter}
            />
          </div>
        );
      })}
    </div>
  );
};

const VERTICAL_BAR_WIDTH = 3;
export const PacingBar = ({ max = 1, parts, color }: PacingBarProps) => {
  const ratio = 100 / max;
  const spendPart = parts.find(part => part.className === 'spend');
  const { val: localSpend } = spendPart || { val: 0 };
  return (
    <div className='item-bar'>
      {parts.map(({ type, label = '', className, val = 0, leftVal = 0 }) => {
        const key = [type, label, className, val].join('_');
        const classes: string[] = [];
        if (className) {
          classes.push(className);
        }
        let style: Record<string, string | number>;
        switch (type) {
          case BarType.HORIZONTAL:
            classes.push('horizontal');
            style = {
              width: `${val * ratio}%`,
              paddingLeft: `${leftVal * ratio}%`,
              backgroundColor: color
            };
            break;
          case BarType.VERTICAL:
            classes.push('vertical');
            style = {
              width: VERTICAL_BAR_WIDTH,
              left: `calc(${val * ratio}%)`
            };
            break;
          case BarType.HORIZONTAL_GREY:
            classes.push('horizontal');
            style = {
              width: `${val * ratio}%`,
              marginLeft: `${(localSpend || 0) * ratio}%`
            };
            break;
          default:
            return undefined;
        }
        return <div key={key} className={cn(classes)} style={style} />;
      })}
    </div>
  );
};

export const DoubleBar = ({ max = 1, parts, color }: PacingBarProps) => {
  const ratio = 100 / max;
  return (
    <div className='double-item-bar'>
      {parts.map(({ type, label = '', className, val = 0, leftVal = 0 }) => {
        const key = [type, label, className, val].join('_');
        const classes: string[] = [];
        if (className) {
          classes.push(className);
        }
        let style: Record<string, string | number>;
        switch (type) {
          case BarType.HORIZONTAL:
            classes.push('horizontal');
            style = {
              width: `${val * ratio}%`,
              paddingLeft: `${leftVal * ratio}%`,
              backgroundColor: color
            };
            break;
          case BarType.HORIZONTAL_GREY:
            classes.push('horizontal grey');
            style = {
              width: `${val * ratio}%`,
              paddingLeft: `${leftVal * ratio}%`
            };
            break;
          default:
            return undefined;
        }
        return <div key={key} className={cn(classes)} style={style} />;
      })}
    </div>
  );
};
